import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from "react";
import {
  useGetPricesByProductQuery,
  useGetProductsQuery,
} from "src/api/services/productsApi";
import LoadingButton from "src/components/Buttons/LoadingButton/LoadingButton";
import { Close } from "@mui/icons-material";
import { desktopSx } from "src/utils/styles/modalStyles";
import {
  useCreateSubscriptionMutation,
  useGetPatientSubscriptionsByIdQuery,
} from "src/api/services/subscriptionsApi";
import { Alert, AlertTitle } from "@mui/material";
import { useGetCouponsForProductQuery } from "src/api/services/couponsApi";
import { useCreatePrescriptionMutation } from "src/api/services/prescriptionsApi";
import { useGetPatientVisitsQuery } from "src/api/services/onlineVisitsApi";
import { isCustomError } from "src/api/types";

interface Props {
  patientId: number;
  onClose: () => void;
  open: boolean;
  patientGender: string | null;
}

const CreateSubscriptionModal = ({
  patientId,
  onClose,
  open,
  patientGender,
}: Props) => {
  const [error, setError] = useState("");
  const [productSlug, setProductSlug] = useState<string | null>(null);
  const [priceId, setPriceId] = useState<string | null>(null);
  const [couponId, setCouponId] = useState<number | null>(null);
  const [createSubscription, { isLoading }] = useCreateSubscriptionMutation();
  const [createPrescription, { isLoading: isPrescriptionLoading }] =
    useCreatePrescriptionMutation();
  const { data: visits = [] } = useGetPatientVisitsQuery({ patientId });
  const { data: prices = [] } = useGetPricesByProductQuery(
    productSlug
      ? {
          slug: productSlug,
          active: true,
        }
      : skipToken,
  );
  const { products } = useGetProductsQuery(undefined, {
    selectFromResult: ({ data }) => {
      return { products: data?.data ?? [] };
    },
  });
  const { data: subscriptions = [] } =
    useGetPatientSubscriptionsByIdQuery(patientId);
  const { data: coupons = [] } = useGetCouponsForProductQuery(
    productSlug ?? skipToken,
  );

  const selectableProducts = useMemo(() => {
    const categories = subscriptions
      .filter((sub) => sub.canceled_at == null)
      .map((sub) => sub.product_category.name);

    return products.filter(
      (product) =>
        !categories.includes(product.category.name) &&
        product.billing_model === "subscription" &&
        (product.gender === patientGender || product.gender == null),
    );
  }, [patientGender, products, subscriptions]);

  const product = useMemo(
    () => selectableProducts.find((product) => product.slug === productSlug),
    [productSlug, selectableProducts],
  );

  const coupon = useMemo(
    () => coupons.find((coupon) => coupon.id === couponId),
    [couponId, coupons],
  );

  const price = useMemo(() => {
    const matched = prices.find((price) => price.stripe_id === priceId);

    if (matched == null) return 0;

    return `$${matched.amount - (coupon?.amount ?? 0)}`;
  }, [coupon?.amount, priceId, prices]);

  const latestVisit = useMemo(() => {
    return visits[visits.length - 1];
  }, [visits]);

  const handleProduct = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setProductSlug(event.target.value);
  }, []);

  const handlePrice = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPriceId(event.target.value);
  }, []);

  const handleCoupon = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "none") {
      setCouponId(null);
      return;
    }

    setCouponId(Number(event.target.value));
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (priceId == null) {
        setError("Please select price");
        return;
      }

      if (latestVisit == null) {
        setError("Looks like patient does not have any visits");
        return;
      }

      if (product == null) {
        setError("Please select product");
        return;
      }

      setError("");

      try {
        await createSubscription({
          patient_id: patientId,
          coupon_id: couponId ?? undefined,
          initial: false,
          price: priceId,
          status: "draft",
        }).unwrap();

        await createPrescription({
          online_visit_id: latestVisit.id,
          patient_id: latestVisit.patient_id,
          status: "created",
          product_id: product.id,
          product_category_id: product.category.id,
        }).unwrap();

        setCouponId(null);
        setProductSlug(null);
        setPriceId(null);
        onClose();
        setError("");
      } catch (err: unknown) {
        if (isCustomError(err)) {
          setError(err.data.message);
        } else {
          console.log(err);
        }
      }
    },
    [
      couponId,
      createPrescription,
      createSubscription,
      latestVisit,
      onClose,
      patientId,
      priceId,
      product,
    ],
  );

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Create subscription
          </Typography>
          <IconButton onClick={onClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Alert severity="warning">
          <AlertTitle>
            You cannot have multiple subscription for the same product category
          </AlertTitle>
          For example: You can only have one subscription for weight loss
        </Alert>

        <Stack
          gap={2}
          component="form"
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <TextField
            select
            required
            label="Product"
            value={productSlug ?? ""}
            onChange={handleProduct}
          >
            {selectableProducts.map((product) => (
              <MenuItem key={product.slug} value={product.slug}>
                {product.name}
              </MenuItem>
            ))}
          </TextField>

          {productSlug ? (
            <Stack gap={2}>
              <TextField
                select
                label="Price"
                value={priceId ?? ""}
                onChange={handlePrice}
                required
              >
                {prices.map((price) => (
                  <MenuItem key={price.id} value={price.stripe_id}>
                    {price.name}
                  </MenuItem>
                ))}
              </TextField>
              {priceId ? (
                <TextField disabled value={price} label="Total" />
              ) : null}
              <TextField
                select
                label="Coupon"
                value={couponId ?? ""}
                onChange={handleCoupon}
              >
                <MenuItem value={"none"}>None</MenuItem>
                {coupons.map((coupon) => (
                  <MenuItem key={coupon.id} value={coupon.id}>
                    {coupon.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          ) : null}

          <Stack gap={1}>
            {error ? (
              <Typography sx={{ color: "red" }} textAlign="center">
                {error}
              </Typography>
            ) : null}

            <LoadingButton
              loading={isLoading || isPrescriptionLoading}
              type="submit"
            >
              Create Subscription
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default CreateSubscriptionModal;
